/**
 * BenchmarkInsights — the "what does this data mean?" interpretation layer.
 *
 * Three presentational pieces:
 *   - ExecutiveSynthesisCard: the top-of-report punchline (headline + why it
 *     matters + summary + at most three top moves).
 *   - TableInsight: the report's guiding principle under each table — what the
 *     data shows, why it matters, how to use it.
 *   - PriorityActionPlan: the closing 5–7 action roadmap.
 *
 * All three render only when the interpretation has content, so reports
 * generated before the interpretation layer — or before the What/Why/How and
 * action-plan fields existed — degrade cleanly rather than showing empty
 * scaffolding. `TableInsight` falls back to the older summary/points shape.
 *
 * HEADING CASE: every heading here is Initial-Caps and must match its
 * counterpart in `reportMarkdown.ts` verbatim (page and document must never
 * drift). "What This Report Means" = §1, "Priority Action Plan" = §11,
 * "Additional Opportunities" and "Potential Follow-On Analysis" = §12. Three of
 * these were sentence-case until 2026-07-19 while the export was already
 * Title-case — the same section read as two different names.
 */
import type {
  ExecutiveSynthesis,
  PriorityAction,
  TableInterpretation,
} from '@aurora/shared';
import { BENCHMARK_SECTION_COPY } from '@aurora/shared';

export function ExecutiveSynthesisCard({
  exec,
}: {
  exec: ExecutiveSynthesis | undefined;
}): JSX.Element | null {
  if (
    !exec ||
    (!exec.headline &&
      !exec.summary &&
      !exec.whyItMatters &&
      (exec.topMoves?.length ?? 0) === 0)
  ) {
    return null;
  }
  return (
    <div className="card interp-exec">
      <h2 className="section-head">
        <span>What This Report Means</span>
      </h2>
      {exec.headline && <p className="interp-headline">{exec.headline}</p>}
      {exec.whyItMatters && (
        <p className="interp-why">
          <span className="interp-why-label">Why it matters</span>
          {exec.whyItMatters}
        </p>
      )}
      {exec.summary && <p>{exec.summary}</p>}
      {exec.topMoves?.length > 0 && (
        <>
          <p className="muted interp-moves-label">Top moves</p>
          <ol className="interp-moves">
            {exec.topMoves.slice(0, 3).map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}

export function TableInsight({
  insight,
}: {
  insight: TableInterpretation | undefined;
}): JSX.Element | null {
  if (!insight) return null;
  const hasWwh = Boolean(
    insight.whatItShows || insight.whyItMatters || insight.howToUseIt,
  );
  if (!hasWwh && !insight.summary && (insight.points?.length ?? 0) === 0) {
    return null;
  }
  return (
    <details className="table-insight">
      <summary>What does this mean?</summary>
      <div className="table-insight-body">
        {hasWwh ? (
          <dl className="wwh">
            {insight.whatItShows && (
              <>
                <dt>What the data shows</dt>
                <dd>{insight.whatItShows}</dd>
              </>
            )}
            {insight.whyItMatters && (
              <>
                <dt>Why it matters</dt>
                <dd>{insight.whyItMatters}</dd>
              </>
            )}
            {insight.howToUseIt && (
              <>
                <dt>How to use it</dt>
                <dd>{insight.howToUseIt}</dd>
              </>
            )}
          </dl>
        ) : (
          insight.summary && <p>{insight.summary}</p>
        )}
        {/* Legacy shape — only stored pre-2026-07-11 interpretations carry it. */}
        {(insight.points?.length ?? 0) > 0 && (
          <ul>
            {(insight.points ?? []).map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}

/** Effort/impact/horizon read as small pills so the table scans at a glance. */
function ratingPill(
  kind: 'effort' | 'impact',
  value: PriorityAction['effort'],
): JSX.Element {
  // High impact is good news; high effort is not. Colour accordingly.
  const good = kind === 'impact' ? value === 'high' : value === 'low';
  const bad = kind === 'impact' ? value === 'low' : value === 'high';
  const cls = good ? 'pill-complete' : bad ? 'pill-draft' : 'pill-pending';
  return <span className={`pill ${cls}`}>{value}</span>;
}

const HORIZON_LABELS: Record<PriorityAction['horizon'], string> = {
  now: 'This quarter',
  next: 'Next quarter',
  later: 'Later',
};

export function PriorityActionPlan({
  actions,
  additionalOpportunities,
  followOnAnalysis,
}: {
  actions: PriorityAction[] | undefined;
  /** Ideas that did not make the top 5 — rendered below the plan, if any. */
  additionalOpportunities?: string[];
  /**
   * The single consolidated place Aurora's follow-on reports are named (Joan
   * 2026-07-19). MUST render wherever the action plan renders: the per-table
   * interpretations no longer mention follow-on reports at all, so if this
   * were dropped the recommendation would vanish from the report entirely.
   */
  followOnAnalysis?: { summary?: string; recommended?: string[] };
}): JSX.Element | null {
  const hasFollowOn =
    !!followOnAnalysis?.summary ||
    (followOnAnalysis?.recommended?.length ?? 0) > 0;
  const hasExtras = (additionalOpportunities?.length ?? 0) > 0;
  if ((!actions || actions.length === 0) && !hasFollowOn && !hasExtras)
    return null;
  return (
    <>
      {actions && actions.length > 0 && (
        <>
          <h2 className="section-head">Priority Action Plan</h2>
          {/* Joan's verbatim framing (17-July doc §8, "Suggested section
              framing"), shared with the Markdown export — which rendered the
              table with NO framing paragraph at all until 2026-07-19. */}
          <p className="section-note">
            {BENCHMARK_SECTION_COPY.actionPlan.intro}
          </p>
          <table className="leads-table action-plan-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Action</th>
                <th>Why</th>
                <th>Evidence</th>
                <th>Effort</th>
                <th>Impact</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {actions.map((a) => (
                <tr key={a.priority}>
                  <td>{a.priority}</td>
                  <td className="lead-name">{a.action}</td>
                  <td>{a.rationale}</td>
                  <td className="muted">{a.evidence}</td>
                  <td>{ratingPill('effort', a.effort)}</td>
                  <td>{ratingPill('impact', a.impact)}</td>
                  <td>{HORIZON_LABELS[a.horizon]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {hasExtras && (
        <details className="additional-opportunities">
          <summary>
            Additional Opportunities ({additionalOpportunities?.length})
          </summary>
          <ul className="insight-moves">
            {additionalOpportunities?.map((o, i) => (
              <li key={i}>{o}</li>
            ))}
          </ul>
        </details>
      )}
      {hasFollowOn && (
        <>
          <h2 className="section-head">Potential Follow-On Analysis</h2>
          {followOnAnalysis?.summary && (
            <p className="section-note">{followOnAnalysis.summary}</p>
          )}
          {(followOnAnalysis?.recommended?.length ?? 0) > 0 && (
            <ul className="insight-moves">
              {followOnAnalysis?.recommended?.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </>
  );
}
